import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import InputForm from '../InputForm';
import useNotificacionesStore from '../../store/notificacionesStore';
import SelectForm from '../SelectForm';
import EditorNotificaciones from './EditorNotificaciones';

const FormNotificaciones = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    
    const { 
        datos, 
        handleChange, 
        handleSave, 
        error, 
        uploadingImage,
        subirImagen,
        tipos_notificacion, 
        partidas,
        productos,
    } = useNotificacionesStore();

    const goBack = () => {
        navigate(-1);
    };

    const handleImageChange = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            
            // Validar tamaño (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('La imagen no debe superar los 5MB');
                return;
            }
            
            // Validar tipo
            if (!file.type.startsWith('image/')) {
                alert('Por favor selecciona una imagen válida');
                return;
            }
            
            try {
                await subirImagen(file);
            } catch (error) {
                alert('Error al subir la imagen. Intenta de nuevo.');
            }
        }
    };

    const removeImage = () => {
        handleChange({ target: { name: 'imagen', value: '' } });
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden">
            <div className="card-body p-40">
                <div className="row">
                    {/* Título */}
                    <InputForm
                        name="titulo"
                        label="Titulo"
                        datos={datos}
                        change={handleChange}
                        error={error}
                        required/>
                    
                    {/* Subtítulo (descripción corta para push) */}
                    <InputForm
                        name="subtitulo"
                        label="Descripción corta (para push notification)"
                        datos={datos}
                        change={handleChange}
                        error={error}
                        required/>

                    {/* Imagen */}
                    <div className="col-12 mb-4">
                        <label className="form-label fw-semibold text-primary-light mb-2">
                            Imagen de la notificación
                        </label>
                        <div className="d-flex flex-column gap-3">
                            {datos.imagen ? (
                                <div className="position-relative" style={{ maxWidth: '400px' }}>
                                    <img 
                                        src={datos.imagen} 
                                        alt="Preview" 
                                        className="img-fluid rounded"
                                        style={{ maxHeight: '200px', objectFit: 'cover' }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                                        onClick={removeImage}
                                    >
                                        <i className="ri-close-line"></i>
                                    </button>
                                </div>
                            ) : (
                                <div 
                                    className="border border-dashed rounded p-4 text-center cursor-pointer"
                                    style={{ borderColor: 'var(--neutral-400)', cursor: 'pointer' }}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <i className="ri-image-add-line fs-2 text-muted"></i>
                                    <p className="text-muted mb-0 mt-2">
                                        {uploadingImage ? 'Subiendo imagen...' : 'Haz clic para agregar una imagen'}
                                    </p>
                                    <small className="text-muted">Máximo 5MB (JPG, PNG)</small>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                style={{ display: 'none' }}
                                disabled={uploadingImage}
                            />
                            {!datos.imagen && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary w-fit"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploadingImage}
                                >
                                    {uploadingImage ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Subiendo...
                                        </>
                                    ) : (
                                        <>
                                            <i className="ri-upload-2-line me-2"></i>
                                            Seleccionar imagen
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Checkbox para activar contenido detallado */}
                    <div className="col-12 mb-3">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="tiene_contenido_detallado"
                                name="tiene_contenido_detallado"
                                checked={datos.tiene_contenido_detallado}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="tiene_contenido_detallado">
                                Incluir contenido detallado (página completa en la app)
                            </label>
                        </div>
                    </div>

                    {/* Contenido enriquecido - solo se muestra si está activado */}
                    {datos.tiene_contenido_detallado && (
                        <div 
                            className="col-12" 
                            style={{ 
                                marginBottom: '40px',
                                position: 'relative',
                                zIndex: 1
                            }}
                        >
                            <EditorNotificaciones
                                name="descripcion"
                                label="Contenido detallado (se mostrará en la app)"
                                value={datos.descripcion}
                                onChange={handleChange}
                                error={error?.descripcion}
                            />
                        </div>
                    )}

                    {/* Tipo de notificación */}
                    <SelectForm
                        name='tipo'
                        label='Tipo de notificación'
                        datos={datos}
                        change={handleChange}
                        options={tipos_notificacion}
                    />

                    {datos.tipo === "partida" && (
                        <SelectForm
                            name="tipoId"
                            label="Partida"
                            datos={datos}
                            change={handleChange}
                            options={partidas}
                            error={error}
                            required
                        />
                    )}

                    {datos.tipo === "producto" && (
                        <SelectForm
                            name="tipoId"
                            label="Producto"
                            datos={datos}
                            change={handleChange}
                            options={productos}
                            error={error}
                            required
                        />
                    )}

                    {error?.general && (
                        <div className="alert alert-warning" role="alert">
                            {error.general}
                        </div>
                    )}

                    {/* Preview de la notificación */}
                    <div className="col-12 mt-4 mb-4">
                        <div className="card bg-neutral-50">
                            <div className="card-body">
                                <h6 className="text-muted mb-3">Vista previa en dispositivo:</h6>
                                <div className="d-flex align-items-start gap-3 p-3 bg-white rounded shadow-sm">
                                    {datos.imagen && (
                                        <img 
                                            src={datos.imagen} 
                                            alt="" 
                                            className="rounded"
                                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                        />
                                    )}
                                    <div className="flex-1">
                                        <h6 className="mb-1 fw-bold">{datos.titulo || 'Título de la notificación'}</h6>
                                        <p className="mb-0 text-muted small">{datos.subtitulo || 'Descripción corta que aparecerá en la push notification...'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Botones de acción - fuera del flujo del editor para evitar interferencias */}
                    <div 
                        className="d-flex align-items-center justify-content-between gap-3 mt-24"
                        style={{ 
                            position: 'relative', 
                            zIndex: 1000,
                            pointerEvents: 'auto'
                        }}
                    >
                        <button
                            onClick={goBack}
                            type="button"
                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                            style={{ pointerEvents: 'auto' }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            disabled={uploadingImage}
                            onClick={handleSave}
                            className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                            style={{ pointerEvents: 'auto' }}
                        >
                            {uploadingImage ? 'Subiendo imagen...' : 'Guardar y Enviar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default FormNotificaciones;
